import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { AnimatedNumber } from '#src/atoms/animations/AnimatedNumber/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { getShardColor } from '#src/set/index.ts';
import { thinMaterial } from '#src/style/utils.tsx';

import type { EssenceBalance_cardSet } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  ${thinMaterial}
  border-radius: var(--unit);
  padding: var(--half-unit) var(--intermediate-unit);
  background: var(--c-nd-50);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);

  & svg {
    width: var(--unit);
    height: var(--unit);
  }
  &button {
    &:hover,
    &:focus {
      background: var(--c-nd-200);
    }
  }

  &.vertical {
    flex-direction: column;
    padding: var(--half-unit);
    gap: 0;
    background: transparent;
  }
`;

type Props = {
  value: number;
  rarity: Rarity;
  cardSet?: Nullable<EssenceBalance_cardSet>;
  to?: ComponentProps<typeof ButtonBase>['to'];
  variant?: 'vertical' | 'horizontal';
  color?: 'tertiary';
};
export const EssenceBalance = ({
  value,
  rarity,
  cardSet,
  to,
  variant,
  color,
}: Props) => {
  const shardColor = getShardColor(rarity, cardSet?.branding ?? null);
  const amount = (
    <AnimatedNumber
      value={value}
      disableAnimationOnFirstRender
      formatNumberProps={{
        notation: 'compact',
        maximumFractionDigits: 1,
      }}
    />
  );

  if (color === 'tertiary') {
    return (
      <Button
        size="small"
        color="tertiary"
        {...(to ? { to } : { asSpan: true })}
        endIcon={<Shards color={shardColor} size={2} />}
      >
        {amount}
      </Button>
    );
  }

  if (variant === 'vertical') {
    return (
      <Root {...(to ? { to, as: ButtonBase } : {})} className="vertical">
        <Vertical center gap={0.5}>
          <Shards color={shardColor} size={2} />
          <LabelS bold>{amount}</LabelS>
        </Vertical>
      </Root>
    );
  }

  return (
    <Root {...(to ? { to, as: ButtonBase } : {})}>
      <Horizontal gap={0.5}>
        <LabelM bold>{amount}</LabelM>
        <Shards color={shardColor} size={2} />
      </Horizontal>
    </Root>
  );
};

EssenceBalance.fragments = {
  cardSet: gql`
    fragment EssenceBalance_cardSet on CardSet {
      slug
      branding {
        id
        colors {
          main
        }
      }
    }
  ` as TypedDocumentNode<EssenceBalance_cardSet>,
};
